<script lang="ts" setup>
import EyeHeader from '@/components/EyeHeader.vue'
import CexNotFound from '@/views/CexNotFound.vue'
import Button from 'primevue/button'
import Skeleton from 'primevue/skeleton'
import Badge from 'primevue/badge'
import Card from 'primevue/card'
import { useTemplateRef, nextTick, ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useThemeStore } from '@/stores/themeStore'
import { useSpreadBellStore } from '@/stores/spreadBellStore'
import EyeOhlcvCard from '@/components/OhlcvCard.vue'
import type { OhlcvChartProps } from '@/components/OhlcvCard.vue'
import ApiKeysModal from '@/components/modals/ApiKeysModal.vue'
import UpdateAmountModal from '@/components/modals/UpdateAmountModal.vue'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import type { UserSettingsUpdate } from '@/services/user-v2-service/types'
import { useRoute, useRouter } from 'vue-router'
import { useEyeStore } from '@/stores/eyeStore'
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import type {
  MarketType,
} from '@/utils/types'
import {
  getTradeUrl,
  toK,
  lcm,
  getBuyMarketType,
  getSellMarketType,
} from '@/utils'
import { buildSymbol, DEFAULT_QUOTE } from '@/utils/quoteHelpers'
import {
  CEX_CEX_MARKET_TYPES,
  AVAILABLE_SWAP_EXCHANGES,
  AVAILABLE_SPOT_EXCHANGES,
  NON_CONTRACT_EXS,
  TICKS_LIMIT,
} from '@/utils/consts'
import CreateSwapOrdersModal from '@/components/modals/CexCexOrdersModal.vue'
import AutoTradeTriggerCreateModal from '@/components/modals/AutoTradeTriggerCreateModal.vue'
// import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import { spreadHelper } from '@/utils'
import type { SymbolFundingRate } from '@/utils/types'
import BellPopover from '@/components/BellPopover.vue'
import type { FormSubmitEvent } from '@primevue/forms'
import { useFlasherStore } from '@/stores/flasherStore'
import Decimal from 'decimal.js'
import { useFavSymbolsStore } from '@/stores/favSymbolsStore'
import UserProfilesInfoModal from '@/components/modals/UserPositionsModal.vue'
import { useCexPositionsRefresher } from '@/composable/refreshers/useCexPositionsRefresher'
import { useCexUpdateMarketsRefresher } from '@/composable/refreshers/useCexUpdateMarketsRefresher'
import { useAutoRefresh } from '@/composable/useAutorefresh'
import { useFavoriteSymbols } from '@/composable/useFavoriteSymbols'
import { useCexCexSpread } from '@/composable/cex-cex/useCexCexSpread'
import type { CexOhlcvResponse } from '@/services/dex-pools-v2-service/types'
import { useNavHeightRefresher } from '@/composable/refreshers/useNavHeightRefresher'
import { TickUtils, type Tick } from '@/utils/ticks'

import { useCexCexPriceLinesUpdater } from '@/composable/cex-cex/useCexCexPriceLinesUpdater'
import { useTrailingSlSettings } from '@/composable/settings/useTrailingSlSettings'
import { useAverageSpreadSettings } from '@/composable/settings/useAverageSpreadSettings'
import { useEyeTickPusher } from '@/composable/useEyeTickPusher'
import { useCheckUserRights } from '@/composable/useCheckUserRights'
import ExchangePriceHeader from '@/components/ExchangePriceHeader.vue'
import SpreadCharts from '@/components/SpreadCharts.vue'

// Сторы
const flasherStore = useFlasherStore()
const themeStore = useThemeStore()
const route = useRoute()
const router = useRouter()
const eyeStore = useEyeStore()
const authStore = useAuthStore()
const userStore = useUserStore()
const bellStore = useSpreadBellStore()
const favSymbolStore = useFavSymbolsStore()

// UI-компоненты
const commonUrl = computed(() => router.resolve({
  name: 'eye-all-asset',
  params: {
    asset: asset.value,
  },
}).fullPath)
const leftChartProps = ref<OhlcvChartProps>({ loading: true, minMove: 4 })
const rightChartProps = ref<OhlcvChartProps>({ loading: true, minMove: 4 })
const navWrapper = ref<HTMLDivElement>()
const colorSchemeIcon = computed(() => (themeStore.mode == 'dark' ? 'pi pi-moon' : 'pi pi-sun'))
const userProfilesModal = useTemplateRef('userProfilesModal')
const updateAmountModal = useTemplateRef('updateAmountModal')
const apiKeysModal = useTemplateRef('apiKeysModal')
const createSwapOrdersModal = useTemplateRef('createSwapOrdersModal')
const autoTradeTriggerEyeModal = useTemplateRef('autoTradeTriggerEyeModal')
const spreadCharts = useTemplateRef('spreadCharts')
const eyeCardHeight = ref('350px')
const tabTitle = computed(() => {
  if (asset.value && exBuy.value && exSell.value) {
    const parts = [
      !isNull(spread.value) ? Number(spread.value).toFixed(2) + '%' : '-',
      !isNull(backSpread.value) ? Number(backSpread.value).toFixed(2) + '%' : '-',
      asset.value.toUpperCase(),
      exBuy.value.toUpperCase(),
      exSell.value.toUpperCase(),
    ]
    if (flasherStore.flash) {
      return '[!!!] ' + parts.join(' / ')
    }
    return parts.join(' / ')
  }
  return 'Fobium.Trade'
})
// UI-переменные
const commonStep = computed(() => {
  const marketList = markets.value
  if (!marketList || !amount.value) {
    return null
  }
  const marketBuy = marketList[exBuy.value]
  const marketSell = marketList[exSell.value]

  let ctrBuy = marketBuy?.contract_size ?? 1
  if (leftChartProps.value.marketType == 'spot') {
    ctrBuy = marketBuy?.precision_sf?.amount ?? 1
  } else if (NON_CONTRACT_EXS.includes(marketBuy?.exchange as string)) {
    ctrBuy = marketBuy?.precision?.amount ?? 1
  }

  let ctrSell = marketSell?.contract_size ?? 1
  if (rightChartProps.value.marketType == 'spot') {
    ctrSell = marketSell?.precision_sf?.amount ?? 1
  } else if (NON_CONTRACT_EXS.includes(marketSell?.exchange as string)) {
    ctrSell = marketSell?.precision?.amount ?? 1
  }

  const step = lcm(ctrBuy, ctrSell)
  return step
})
const recommendedAmount = computed(() => {
  if (!priceSell.value) {
    return
  }
  const step = commonStep.value
  if (!step) {
    return
  }
  const decA = new Decimal(amount.value)
  const decPrice = new Decimal(priceSell.value)
  const am = decA.div(decPrice).div(step).floor().mul(step)
  return am.toNumber()
})
const commonMinMove = computed(() => {
  return leftChartProps.value.minMove < rightChartProps.value.minMove ? leftChartProps.value.minMove : rightChartProps.value.minMove
})
const fundingRates = computed(() => {
  const res: Record<string, SymbolFundingRate> = {}
  if (leftChartProps.value.exchange) {
    res[leftChartProps.value.exchange] = {
      rate: leftChartProps.value.fundingRate ?? leftChartProps.value.fundingRate,
      period: leftChartProps.value.fundingPeriod,
    }
  }
  if (rightChartProps.value.exchange) {
    res[rightChartProps.value.exchange] = {
      rate: rightChartProps.value.fundingRate ?? rightChartProps.value.fundingRate,
      period: rightChartProps.value.fundingPeriod,
    }
  }
  return res
})
const assetNotFound = ref(false)
const asset = computed(() => route.params.asset as string)
const marketType = computed(() => route.params.marketType as MarketType)

const techSymbols = computed(() => [
  buildSymbol(route.params.asset as string, quoteBuy.value, buyMarketType.value),
  buildSymbol(route.params.asset as string, quoteSell.value, sellMarketType.value)
])

const exBuy = computed(() => (route.params.exBuy as string).toLowerCase())
const exSell = computed(() => (route.params.exSell as string).toLowerCase())

const { isCexCex } = useCheckUserRights()

const isPlacingOrdersAvailable = computed(() => {
  const leftExSource = ["spot", "spot-swap"].includes(marketType.value) ? AVAILABLE_SPOT_EXCHANGES : AVAILABLE_SWAP_EXCHANGES
  const isLongAvailable = leftExSource.includes(exBuy.value)
  return CEX_CEX_MARKET_TYPES.includes(marketType.value) && isCexCex.value && isLongAvailable
})

const isTwoLegsModeActive = computed(() => {
  const rightExSource = ["swap", "swap-swap", "spot-swap"].includes(marketType.value) ? AVAILABLE_SWAP_EXCHANGES : AVAILABLE_SPOT_EXCHANGES
  const isShortAvailable = rightExSource.includes(exSell.value)
  const isModeGood = ["swap", "swap-swap", "spot-swap"].includes(marketType.value)
  return isPlacingOrdersAvailable.value && isShortAvailable && isModeGood
})

const userDefaultAmount = computed(() => {
  if (marketType.value == 'spot-swap') {
    return Number(authStore.settings['amount_usdt_spot_futures'] ?? 200)
  } else if (marketType.value == "spot") {
    return Number(authStore.settings['amount_usdt'] ?? 200)
  } else if (marketType.value == "swap") {
    return Number(authStore.settings['amount_usdt_futures'] ?? 200)
  } else {
    return 200
  }
})
const amount = computed(() =>
  route.params.amount ? Number(route.params.amount) : userDefaultAmount.value,
)
const sellAsset = computed(() => (route.query.sell as string) || null)
const sellAssetValue = computed(() => sellAsset.value || asset.value)

const quoteBuy = computed(() => {
  if (exBuy.value == "hyperliquid") {
    // на гипере всё торгуется к USDC
    return 'USDC'
  }
  return (route.query.qb as string) || DEFAULT_QUOTE
})
const quoteSell = computed(() => {
  if (exSell.value == 'hyperliquid') {
    // на гипере всё торгуется к USDC
    return 'USDC'
  }
  return (route.query.qs as string) || DEFAULT_QUOTE
})

const leftExchangeContractSize = computed(() => markets.value?.[exBuy.value]?.contract_size ?? null)
const homeRouteName = computed(() => {
  if (marketType.value == "spot-swap") {
    return "signals-spot-swap"
  } else {
    return "signals-swap"
  }
})
const switchSideUrl = computed(() => {
  return router.resolve({
    name: 'eye',
    params: {
      marketType: route.params.marketType,
      asset: sellAssetValue.value,
      amount: route.params.amount,
      exBuy: route.params.exSell,
      exSell: route.params.exBuy,
    },
    query: {
      sell: asset.value,
      qb: quoteSell.value,
      qs: quoteBuy.value,
    },
  }).href
})
const buyMarketType = computed(() => getBuyMarketType(marketType.value))
const sellMarketType = computed(() => getSellMarketType(marketType.value))

// Символы для API (с _ вместо /)
const buySymbol = computed(() => buildSymbol(asset.value, quoteBuy.value, buyMarketType.value))
const sellSymbol = computed(() => buildSymbol(sellAssetValue.value, quoteSell.value, sellMarketType.value))
// const showServerTriggers = computed(() => "debug" in route.query)

// Композаблы
const { toggleFavoriteSymbol, favoriteId } = useFavoriteSymbols(exBuy, exSell, marketType, asset)
const exBuyPositionsRefresher = useCexPositionsRefresher(exBuy, "cex_cex")
const exSellPositionsRefresher = useCexPositionsRefresher(exSell, "cex_cex")
const { markets, marketsRefresher, updateMarkets } = useCexUpdateMarketsRefresher(marketType, asset, exBuy, exSell, sellAsset, quoteBuy, quoteSell)
const { navHeight, navHeightRefresher } = useNavHeightRefresher(navWrapper)

const leftTicks = ref<Tick[]>([])
const rightTicks = ref<Tick[]>([])

const loadTicks = async () => {
  const [buyTicks, sellTicks] = await Promise.all([
    eyeStore.fetchSymbolTicks(`ticks-${exBuy.value}-${buySymbol.value}`, exBuy.value, buySymbol.value.replace('/', '_')),
    eyeStore.fetchSymbolTicks(`ticks-${exSell.value}-${sellSymbol.value}`, exSell.value, sellSymbol.value.replace('/', '_')),
  ])
  const tillTs = +new Date() / 1000
  if (buyTicks) {
    leftTicks.value = TickUtils.fillTicksTo(buyTicks, tillTs).slice(-TICKS_LIMIT)
  }
  if (sellTicks) {
    rightTicks.value = TickUtils.fillTicksTo(sellTicks, tillTs).slice(-TICKS_LIMIT)
  }
}

const { ohlcvState, linesRefresher } = useCexCexPriceLinesUpdater(exBuy, exSell, buyMarketType, sellMarketType, leftTicks, rightTicks)

/**
 * Последняя цена на бирже справа (биржа продажи)
 */
const latestExSellPrice = computed(() => rightTicks.value[rightTicks.value.length - 1]?.[1] ?? null)

// Обработка обновлений свеч
const onOhlcv = (
  exchange: string,
  marketType: MarketType,
  candle: CexOhlcvResponse,
  timestamp: number,
) => {
  const exchangeKey = `${exchange}_${marketType}`
  ohlcvState.value[exchangeKey] = {
    candle: candle,
    timestamp: timestamp
  }
  const isLeftChart = leftChartProps.value.exchange == exchange && leftChartProps.value.marketType == marketType
  const chart = isLeftChart ? leftChartProps : rightChartProps
  if (chart.value.ohlcv && candle) {
    const result = new Map(chart.value.ohlcv.map((i) => [i[0], i]))
    candle[0] = Math.round(candle[0] / 1000)
    result.set(candle[0], candle)
    const newOhlcv = [...result.values()]
    newOhlcv.sort((a, b) => a[0] - b[0])
    chart.value.ohlcv = newOhlcv
  }
}

const {
  startWebSocket, stopWebSocket, priceBuy, priceSell, backPriceBuy,
  backPriceSell, spread, backSpread, priceBuyLoading,
  priceSellLoading, volSell, volBuy, isSpreadOk,
  isBackSpreadOk, checkBellRefresher
} = useCexCexSpread(exBuy, exSell, marketType, asset, sellAssetValue, amount, onOhlcv, quoteBuy, quoteSell)

// Рефрешер цен asks/bids на графике
const cexAsks = ref<Tick[]>([])
const cexBids = ref<Tick[]>([])
const { refresher: cexAsksRefresher } = useEyeTickPusher(priceBuy, cexAsks, { interval: 1000 })
const { refresher: cexBidsRefresher } = useEyeTickPusher(backPriceSell, cexBids, { interval: 1000 })
const cex2Asks = ref<Tick[]>([])
const cex2Bids = ref<Tick[]>([])
const { refresher: cex2AsksRefresher } = useEyeTickPusher(priceSell, cex2Bids, { interval: 1000 })
const { refresher: cex2BidsRefresher } = useEyeTickPusher(backPriceBuy, cex2Asks, { interval: 1000 })

// Рефрешер данных графиков
const updateChartsMetaData = async () => {
  updateChartMetaData(leftChartProps.value)
  updateChartMetaData(rightChartProps.value)
}
const metaDataRefresher = useAutoRefresh(updateChartsMetaData, 1000)

const updateChart = async (chartData: OhlcvChartProps) => {
  if (!chartData.exchange || !chartData.marketType || !asset.value) {
    return
  }
  const isSell = chartData.exchange === exSell.value
  const chartAsset = isSell ? sellAssetValue.value : asset.value
  const actionId = `${chartData.exchange}/${chartAsset}/${chartData.marketType}`.toLocaleLowerCase()
  if (eyeStore.error[actionId]) {
    chartData.loading = true
  }
  const chartQuote = isSell ? quoteSell.value : quoteBuy.value
  const data = await eyeStore.fetchOhlcv(
    actionId,
    chartData.marketType,
    chartAsset,
    chartData.exchange,
    '1m',
    chartQuote,
  )
  chartData.error = !isNull(eyeStore.error[actionId]) && !isUndefined(eyeStore.error[actionId])
  if (data) {
    chartData.ohlcv = data
    chartData.loading = false

    const ohlcvCacheKey = `${chartData.exchange}_${chartData.marketType}`
    if (ohlcvCacheKey in ohlcvState.value) {
      const latestCandle = data[data.length - 1]
      if (latestCandle) {
        ohlcvState.value[ohlcvCacheKey] = {
          candle: latestCandle,
          timestamp: latestCandle[0]
        }
      }
    }
  }
}

const updateChartMetaData = (chartData: OhlcvChartProps) => {
  const marketList = markets.value
  if (!marketList || !chartData.exchange || !(chartData.exchange in marketList)) {
    chartData.vol25hError = true
    chartData.fundingError = true
    return
  }

  const useSfPostfix = chartData.marketType === 'spot' && ["spot-swap", "swap", "swap-spot"].includes(marketType.value)
  const volKey = useSfPostfix ? 'vol24h_sf' : 'vol24h'
  const precisionKey = useSfPostfix ? 'precision_sf' : 'precision'

  const thisMarket = marketList[chartData.exchange]
  chartData.minMove = Number(thisMarket?.[precisionKey]?.price) ?? 0.00000001
  if (thisMarket?.funding_rate?.next_ts) {
    chartData.fundingRate = thisMarket?.funding_rate?.current * 100
    const now = +new Date()
    const nextTs = thisMarket?.funding_rate?.next_ts ?? 0
    const timeLeft = Math.round((nextTs - now) / 1000)
    chartData.fundingPeriod = timeLeft
    if (chartData.fundingPeriod <= 0) {
      chartData.fundingError = true
    } else {
      chartData.fundingError = false
    }
  } else {
    chartData.fundingError = true
  }
  if (thisMarket?.[volKey]) {
    chartData.vol24h = toK(thisMarket?.[volKey])
  } else {
    chartData.vol25hError = true
  }
}

// Для режима работы в одну ногу.
const cacheKeyPostfix = computed(() => `cex/cex/${exBuy.value}/${exSell.value}/${asset.value}/${quoteBuy.value}/${quoteSell.value}`.toLocaleLowerCase())
const { trailingExit, trailingNext } = useTrailingSlSettings(cacheKeyPostfix)
const { avgSpread, autoAvgSpread } = useAverageSpreadSettings(cacheKeyPostfix)

const spreads = computed(() => spreadCharts.value?.spreads ?? [])
const calcAvgSpread = async () => {
  if (!autoAvgSpread.value) {
    return
  }
  const spreadsOnly = spreads.value.map(x => x[1])
  const mean = spreadsOnly.reduce((acc, val) => acc + val, 0) / spreadsOnly.length;
  if (mean) {
    avgSpread.value = mean
  }
}

const avgSpreadRefresher = useAutoRefresh(calcAvgSpread, 5000)

// Вотчеры
watch(autoAvgSpread, (v) => {
  if (!v) {
    avgSpread.value = null
  } else {
    calcAvgSpread()
  }
})
watch(tabTitle, (v) => {
  if (v) {
    window.document.title = v
  }
})
watch(
  () => authStore.currentAccessList,
  () => {
    if (!isCexCex.value) {
      themeStore.forbidden = true
      return
    }
  },
)

const updatePositions = () => {
  exBuyPositionsRefresher.updatePositions()
  exSellPositionsRefresher.updatePositions()
}

onUnmounted(() => {
  navHeightRefresher.stopRefresh()
  linesRefresher.stopRefresh()
  exBuyPositionsRefresher.positionsRefresher.stopRefresh()
  exSellPositionsRefresher.positionsRefresher.stopRefresh()
  checkBellRefresher.stopRefresh()
  avgSpreadRefresher.stopRefresh()
  cexAsksRefresher.stopRefresh()
  cex2AsksRefresher.stopRefresh()
  cexBidsRefresher.stopRefresh()
  cex2BidsRefresher.stopRefresh()
  metaDataRefresher.stopRefresh()
  stopWebSocket()
})

const initCharts = () => {
  if (exBuy.value) {
    leftChartProps.value.exchange = exBuy.value
  }
  if (exSell.value) {
    rightChartProps.value.exchange = exSell.value
  }
  leftChartProps.value.marketType = getBuyMarketType(marketType.value)
  rightChartProps.value.marketType = getSellMarketType(marketType.value)
  leftChartProps.value.hideFundingRate = getBuyMarketType(marketType.value) === 'spot'
  rightChartProps.value.hideFundingRate = getSellMarketType(marketType.value) === 'spot'
  if (leftChartProps.value.exchange) {
    leftChartProps.value.titleHref = getTradeUrl(
      leftChartProps.value.exchange,
      asset.value,
      leftChartProps.value.marketType == 'swap',
      quoteBuy.value,
    )
  }
  if (rightChartProps.value.exchange) {
    rightChartProps.value.titleHref = getTradeUrl(
      rightChartProps.value.exchange,
      sellAssetValue.value,
      rightChartProps.value.marketType == 'swap',
      quoteSell.value,
    )
  }
}

const updateCharts = async () => {
  const promises = [
    updateChart(leftChartProps.value),
    updateChart(rightChartProps.value)
  ]
  await Promise.allSettled(promises)
}

onMounted(async () => {
  checkBellRefresher.startRefresh()
  navHeightRefresher.startRefresh()
  eyeStore.clearActions()
  await updateMarkets()
  const m = markets.value
  if (!m || !m[exBuy.value] || !m[exSell.value]) {
    assetNotFound.value = true
    return
  }
  initCharts()
  updateChartsMetaData()
  await loadTicks()
  startWebSocket()
  linesRefresher.startRefresh()
  cexAsksRefresher.startRefresh()
  cexBidsRefresher.startRefresh()
  cex2AsksRefresher.startRefresh()
  cex2BidsRefresher.startRefresh()
  await updateCharts()
  marketsRefresher.startRefresh()
  metaDataRefresher.startRefresh()
  exBuyPositionsRefresher.updatePositions()
  exBuyPositionsRefresher.positionsRefresher.startRefresh()
  exSellPositionsRefresher.updatePositions()
  exSellPositionsRefresher.positionsRefresher.startRefresh()
  avgSpreadRefresher.startRefresh()
  bellStore.cleanState()
  await nextTick()
  calcAvgSpread()
})

const AMOUNT_KEY_MAP: Record<string, string> = {
  'spot-swap': 'amount_usdt_spot_futures',
  'spot': 'amount_usdt',
  'swap': 'amount_usdt_futures',
  'swap-spot': 'amount_usdt_futures_spot',
}

const onAmountFormSubmit = async (r: FormSubmitEvent) => {
  const valid = r.valid
  const values = r.values
  if (valid) {
    const amountKey = AMOUNT_KEY_MAP[marketType.value] || 'amount_usdt_futures'
    const data = {
      amount_usdt: null,
      amount_usdt_futures: null,
      amount_usdt_spot_futures: null,
      amount_usdt_spot_spot: null,
      amount_usdt_swap_spot: null,
      amount_usdt_futures_spot: null,
      enable_alerts_sf: null,
      enable_alerts_dc: null,
      enable_alerts_dc_spot: null,
      enable_alerts_ff: null,
      enable_alerts_ss: null,
      enable_alerts_fs: null,
    } as UserSettingsUpdate
      ; (data as Record<string, number | null>)[amountKey] = Number(values.amount)
    await userStore.saveUserSettings(data)
    authStore.settings[amountKey] = String(values.amount)

    const query: Record<string, string> = {}
    if (sellAsset.value) query.sell = sellAsset.value
    query.qb = quoteBuy.value
    query.qs = quoteSell.value
    window.location.href = router.resolve({
      name: 'eye',
      params: {
        marketType: marketType.value,
        asset: asset.value,
        amount: values.amount,
        exBuy: exBuy.value,
        exSell: exSell.value,
      },
      query,
    }).href
  }
}

const openPositionsModal = () => {
  userProfilesModal.value?.toggle()
}
</script>

<template>
  <div>
    <CexNotFound v-if="assetNotFound" />
    <div v-show="!assetNotFound">
      <UserProfilesInfoModal :exchanges="[exBuy, exSell]" :symbols="techSymbols" ref="userProfilesModal">
      </UserProfilesInfoModal>
      <UpdateAmountModal @submit="onAmountFormSubmit" ref="updateAmountModal"></UpdateAmountModal>
      <ApiKeysModal keys-type="cex_cex" ref="apiKeysModal"></ApiKeysModal>
      <CreateSwapOrdersModal :recommended-amount="recommendedAmount" :ex-buy="exBuy" :ex-sell="exSell" :spread="spread"
        :back-spread="backSpread" :price-buy="priceBuy" :price-sell="priceSell" :back-price-buy="backPriceSell"
        :back-price-sell="backPriceBuy" :vol-buy="volBuy" :vol-sell="volSell" :funding-rates="fundingRates"
        :quote-buy="quoteBuy" :quote-sell="quoteSell" ref="createSwapOrdersModal" :asset="asset" :sell-asset="sellAsset"
        :market-type="marketType" :amount-step="commonStep" :rightTicks :contract-size="leftExchangeContractSize"
        @show-positions-modal="openPositionsModal" :two-legs="isTwoLegsModeActive"
        @update-user-positions="updatePositions" :latestExSellPrice="latestExSellPrice" :cache-key="cacheKeyPostfix">
      </CreateSwapOrdersModal>
      <AutoTradeTriggerCreateModal :ex-buy="exBuy" :ex-sell="exSell" :asset="asset" :sell-asset="sellAssetValue"
        :market-type="marketType" :amount="amount" :quote-buy="quoteBuy" :quote-sell="quoteSell"
        ref="autoTradeTriggerEyeModal">
      </AutoTradeTriggerCreateModal>
      <div ref="navWrapper" class="sr-navbar-container">
        <EyeHeader>
          <template #start>
            <span v-if="recommendedAmount" class="pit digit amount" @click="updateAmountModal?.toggle()">
              {{ recommendedAmount }} {{ asset.toUpperCase() }} (${{ Number(amount).toFixed(0) }})
            </span>
            <template v-else>
              <Skeleton v-tooltip="$t('tooltip.noSpreadByAmount')" width="100px" height="22px"></Skeleton>
              <Skeleton v-tooltip="$t('tooltip.noSpreadByAmount')" class="ms-3" width="50px" height="22px">
              </Skeleton>
            </template>
          </template>

          <!-- Биржа, цена и объём -->
          <template #exBuy>
            <ExchangePriceHeader :exchange="exBuy" :title-href="leftChartProps.titleHref" :price="priceBuy"
              :vol="volBuy" :loading="priceBuyLoading" price-class="mark-buy" class="sr-eye-ex-buy">
            </ExchangePriceHeader>
          </template>

          <!-- Торговая пара -->
          <template #symbol>
            <a :href="commonUrl" target="_blank" class="uppercase pit hover">{{ asset }}</a>
          </template>

          <!-- Спред и обратный спред -->
          <template #spread>
            <div class="flex justify-center items-center" :style="`height: 30px`">
              <template v-if="priceBuyLoading || priceSellLoading">
                <Skeleton height="22px" width="50px"></Skeleton>
                <Skeleton height="22px" class="ms-2" width="40px"></Skeleton>
              </template>
              <template v-else>
                <span class="sr-eyebar-spread flex items-center digit">
                  <template v-if="isSpreadOk"> {{ spreadHelper(spread) }}% </template>
                  <template v-else>
                    <Badge v-tooltip="$t('tooltip.noSpreadByAmount')" value="!!!" severity="danger">
                    </Badge>
                  </template>
                </span>
                <span class="ms-2 sr-eyebar-backspread flex items-center digit">
                  <template v-if="isBackSpreadOk">{{ spreadHelper(backSpread) }}% </template>
                  <template v-else>
                    <Badge v-tooltip="$t('tooltip.noBackSpreadByAmount')" value="!!!" severity="danger">
                    </Badge>
                  </template>
                </span>
              </template>
            </div>
          </template>

          <!-- Биржа, цена и объём -->
          <template #exSell>
            <ExchangePriceHeader :exchange="exSell" :title-href="rightChartProps.titleHref" :price="priceSell"
              :vol="volSell" :loading="priceSellLoading" price-class="mark-sell" class="sr-eye-ex-sell">
            </ExchangePriceHeader>
          </template>

          <template #end>
            <div class="sr-eye-navbar-end gap-1 flex items-center">

              <!-- <LanguageSwitcher size="small" /> -->

              <Button size="small" v-tooltip.bottom="$t('goHome')" icon="pi pi-home"
                @click="router.push({ name: homeRouteName })" variant="text" rounded aria-label="Home" />

              <Button size="small" v-tooltip.bottom="$t('changeColorScheme')" :icon="colorSchemeIcon"
                @click="themeStore.toggle()" variant="text" rounded aria-label="Theme" />

              <Button size="small" v-tooltip.bottom="$t('tgNotifications')" icon="pi pi-telegram"
                href="https://t.me/spreadracer_eye_bot" as="a" target="_blank" variant="text" rounded
                aria-label="Theme" />

              <BellPopover size="small" rounded :spread-label="$t('openSpread')" :back-spread-label="$t('closeSpread')">
              </BellPopover>

              <Button size="small" v-tooltip.bottom="$t('apiKeys')" icon="pi pi-key" @click="apiKeysModal?.toggle()"
                variant="text" rounded aria-label="Api Keys" />

              <Button size="small" v-show="isPlacingOrdersAvailable" v-tooltip.bottom="$t('placeOrders')"
                icon="pi pi-bolt" @click="createSwapOrdersModal?.toggle()" variant="text" rounded />

              <!-- <Button size="small" v-show="isPlacingOrdersAvailable && showServerTriggers"
                v-tooltip.bottom="$t('serverTrigger')" icon="pi pi-server" @click="autoTradeTriggerEyeModal?.toggle()"
                variant="text" rounded /> -->

              <Button size="small" v-tooltip.bottom="$t('switchExchanges')" icon="pi pi-arrow-right-arrow-left" as="a"
                :href="switchSideUrl" target="_blank" variant="text" rounded aria-label="Switch exchanges" />

              <Button size="small" v-tooltip.bottom="$t('addToFav')" :disabled="favSymbolStore.loading.fetch"
                :icon="favoriteId === null ? 'pi pi-star' : 'pi pi-star-fill'" @click="toggleFavoriteSymbol"
                variant="text" rounded aria-label="Add to favorite" />
            </div>
          </template>
        </EyeHeader>
      </div>
      <main class="mx-0 mt-0 grid grid-cols-1 lg:grid-cols-2" :style="{ paddingTop: navHeight + 'px' }">
        <Card pt:body:class="p-0" pt:root:class="rounded-none lg:col-span-2" style="border: none;">
          <template #content>
            <SpreadCharts ref="spreadCharts" :ticks-left="leftTicks" :ticks-right="rightTicks" :minMove="commonMinMove"
              :show-ab-ba="true" :tp="trailingNext" :sl="trailingExit" :asks-left="cexAsks" :bids-left="cexBids"
              :asks-right="cex2Asks" :bids-right="cex2Bids" :legend="[
                { name: 'ticks-left', label: 'legend.left' },
                { name: 'ticks-right', label: 'legend.right' },
                { name: 'ask-left', label: 'legend.buy' },
                { name: 'bid-left', label: 'legend.sell' },
                { name: 'ask-right', label: 'legend.buy' },
                { name: 'bid-right', label: 'legend.sell' },
              ]" :ex-buy="exBuy" :ex-sell="exSell">
            </SpreadCharts>
          </template>
        </Card>
        <Card pt:body:class="p-0" pt:root:class="rounded-none" style="border-right: none;border-left:none;">
          <template #content>
            <EyeOhlcvCard :height="eyeCardHeight" v-bind="leftChartProps" title-class="success capitalize"
              y-format="1s">
            </EyeOhlcvCard>
          </template>
        </Card>
        <Card pt:body:class="p-0" pt:root:class="rounded-none" style="border-right: none;">
          <template #content>
            <EyeOhlcvCard :height="eyeCardHeight" v-bind="rightChartProps" title-class="danger capitalize"
              y-format="1s">
            </EyeOhlcvCard>
          </template>
        </Card>
      </main>
    </div>
  </div>
</template>
